import { useEffect, useRef } from "react";

const Hero: React.FC = () => {
	const canvasRef = useRef<HTMLCanvasElement | null>(null);
	const videoRef = useRef<HTMLVideoElement | null>(null);

	useEffect(() => {
		const video = videoRef.current;
		if (video) {
			const handleTimeUpdate = () => {
				if (video.currentTime >= 30) {
					video.currentTime = 6;
					video.play();
				}
			};

			video.addEventListener("timeupdate", handleTimeUpdate);
			return () => video.removeEventListener("timeupdate", handleTimeUpdate);
		}
	}, []);

	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;

		const ctx = canvas.getContext("2d");
		if (!ctx) return;

		let animationId: number;
		const particles: Particle[] = [];

		const resize = (): void => {
			canvas.width = window.innerWidth;
			canvas.height = window.innerHeight;
		};

		resize();
		window.addEventListener("resize", resize);

		class Particle {
			x: number;
			y: number;
			size: number;
			speedY: number;
			speedX: number;
			opacity: number;
			maxOpacity: number;
			fadeIn: boolean;
			life: number;
			maxLife: number;

			constructor(canvasWidth: number, canvasHeight: number) {
				this.x = Math.random() * canvasWidth;
				this.y = canvasHeight + Math.random() * 100;
				this.size = Math.random() * 150 + 50;
				this.speedY = Math.random() * 0.5 + 0.2;
				this.speedX = (Math.random() - 0.5) * 0.3;
				this.opacity = 0;
				this.maxOpacity = Math.random() * 0.08 + 0.02;
				this.fadeIn = true;
				this.life = 0;
				this.maxLife = Math.random() * 500 + 300;
			}

			update(canvasWidth: number, canvasHeight: number): void {
				this.y -= this.speedY;
				this.x += this.speedX;
				this.life++;

				if (this.fadeIn) {
					this.opacity += 0.001;
					if (this.opacity >= this.maxOpacity) {
						this.fadeIn = false;
					}
				} else if (this.life > this.maxLife - 100) {
					this.opacity -= 0.002;
				}

				if (
					this.life >= this.maxLife ||
					this.y < -this.size ||
					this.opacity <= 0
				) {
					this.reset(canvasWidth, canvasHeight);
				}
			}

			reset(canvasWidth: number, canvasHeight: number): void {
				this.x = Math.random() * canvasWidth;
				this.y = canvasHeight + Math.random() * 100;
				this.size = Math.random() * 150 + 50;
				this.speedY = Math.random() * 0.5 + 0.2;
				this.speedX = (Math.random() - 0.5) * 0.3;
				this.opacity = 0;
				this.maxOpacity = Math.random() * 0.08 + 0.02;
				this.fadeIn = true;
				this.life = 0;
				this.maxLife = Math.random() * 500 + 300;
			}

			draw(ctx: CanvasRenderingContext2D): void {
				ctx.save();
				ctx.globalAlpha = Math.max(0, this.opacity);
				const gradient = ctx.createRadialGradient(
					this.x,
					this.y,
					0,
					this.x,
					this.y,
					this.size,
				);
				gradient.addColorStop(0, "rgba(200, 200, 200, 0.3)");
				gradient.addColorStop(0.5, "rgba(150, 150, 150, 0.1)");
				gradient.addColorStop(1, "rgba(100, 100, 100, 0)");
				ctx.fillStyle = gradient;
				ctx.beginPath();
				ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
				ctx.fill();
				ctx.restore();
			}
		}

		// Create particles
		for (let i = 0; i < 25; i++) {
			const particle = new Particle(canvas.width, canvas.height);
			particle.y = Math.random() * canvas.height;
			particles.push(particle);
		}

		const animate = (): void => {
			ctx.clearRect(0, 0, canvas.width, canvas.height);
			particles.forEach((particle) => {
				particle.update(canvas.width, canvas.height);
				particle.draw(ctx);
			});
			animationId = requestAnimationFrame(animate);
		};

		animate();

		return () => {
			window.removeEventListener("resize", resize);
			cancelAnimationFrame(animationId);
		};
	}, []);

	return (
		<section
			id="hero"
			className="relative h-[100dvh] min-h-screen w-full overflow-hidden"
		>
			{/* Background Video */}
			<video
				ref={videoRef}
				autoPlay
				muted
				loop
				playsInline
				className="absolute inset-0 w-full h-full object-cover blur-[4px]"
				style={{
					filter: "blur(4px) brightness(0.45) contrast(1.1) sepia(0.2)",
				}}
				poster="/bg_pcc.png"
			>
				<source src="/video.mp4#t=6,30" type="video/mp4" />
			</video>

			{/* Balanced Golden/Champagne Overlay with Soft Light Blend Mode */}
			<div
				className="absolute inset-0 opacity-35 mix-blend-soft-light"
				style={{ background: "rgba(207, 194, 151, 0.9)" }}
			/>

			{/* Dark overlay for readability */}
			<div className="absolute inset-0 bg-black/45" />

			{/* Smoke Canvas Effect */}
			<canvas
				ref={canvasRef}
				className="absolute inset-0 pointer-events-none"
				style={{ mixBlendMode: "screen" }}
			/>

			{/* Hero Content */}
			<div className="absolute inset-0 flex flex-col items-center justify-center z-10 px-4 text-center">
				<h1 className="text-5xl md:text-8xl lg:text-9xl font-black text-white mb-6 uppercase tracking-tighter drop-shadow-2xl">
					WILD<span className="text-[#cfc297] text-stroke-white">FÖRSTER</span>
				</h1>
				<p className="text-[10px] sm:text-base md:text-xl lg:text-3xl text-white/80 mb-6 max-w-[95vw] sm:max-w-5xl drop-shadow-lg font-light uppercase tracking-[0.15em] sm:tracking-[0.3em] lg:tracking-[0.4em] flex items-center justify-center gap-x-1.5 sm:gap-x-4 lg:gap-x-8 whitespace-nowrap">
					<span>Präzision</span>
					<span className="text-[#cfc297] text-[8px] sm:text-base lg:text-xl transform scale-75 sm:scale-100 opacity-60">
						●
					</span>
					<span>Leidenschaft</span>
					<span className="text-[#cfc297] text-[8px] sm:text-base lg:text-xl transform scale-75 sm:scale-100 opacity-60">
						●
					</span>
					<span>Perfektion</span>
				</p>

				<p className="text-sm md:text-base text-white/60 mb-12 max-w-2xl font-light italic tracking-widest leading-relaxed">
					Wo Leidenschaft auf Perfektion trifft. Wir verwandeln Ihr Fahrzeug in
					ein Kunstwerk –{" "}
					<span className="text-white/80">
						professionell, präzise und kompromisslos in der Qualität.
					</span>
				</p>

				<div className="flex flex-col sm:flex-row gap-6 w-full sm:w-auto items-center">
					<button
						className="group relative px-10 py-4 bg-transparent border border-white/30 text-white rounded-full overflow-hidden transition-all duration-500 hover:border-[#cfc297]"
						onClick={() =>
							document
								.getElementById("works")
								?.scrollIntoView({ behavior: "smooth" })
						}
					>
						<span className="relative z-10 uppercase tracking-[0.2em] font-bold text-sm">
							Unsere Arbeiten
						</span>
						<div className="absolute inset-0 bg-[#cfc297] translate-y-full group-hover:translate-y-0 transition-transform duration-500"></div>
						<span className="absolute inset-0 flex items-center justify-center uppercase tracking-[0.2em] font-bold text-sm text-black opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-20">
							Unsere Arbeiten
						</span>
					</button>

					<button
						className="relative px-12 py-4 bg-[#cfc297] text-black rounded-full overflow-hidden group shadow-[0_0_20px_rgba(207,194,151,0.3)] hover:shadow-[0_0_30px_rgba(207,194,151,0.5)] transition-all duration-500"
						onClick={() =>
							document
								.getElementById("contact")
								?.scrollIntoView({ behavior: "smooth" })
						}
					>
						<span className="relative z-10 uppercase tracking-[0.2em] font-black text-sm">
							Kontakt
						</span>
						<div className="absolute inset-0 bg-white -translate-x-full group-hover:translate-x-0 transition-transform duration-500 opacity-20"></div>
					</button>
				</div>
			</div>

			{/* Scroll indicator */}
			<div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce">
				<div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
					<div className="w-1 h-3 bg-white/50 rounded-full mt-2" />
				</div>
			</div>
		</section>
	);
};

export default Hero;
